import {
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";

interface Department {
  id: number;
  name: string;
}

interface DepartmentOption {
  value: string;
  label: string;
}

interface SubDepartmentFormData {
  subDeptID: number;
  deptID: number;
  subDeptName: string;
  isActive: boolean;
  createdBy: number;
  createdDate: string;
  departmentName: string;
}

const SubDepartmentAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [toaster, setToaster] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced validation schema
  const validationSchema = Yup.object({
    deptID: Yup.number()
      .required(t("text.reqDeptName"))
      .min(1, t("text.invalidDepartmentSelection")),
    subDeptName: Yup.string()
      .required(t("text.reqSubDeptName"))
      .max(50, t("text.subDeptNameMaxLength"))
      .matches(/^[a-zA-Z0-9\s\-&.,()]+$/, t("text.invalidSubDeptName"))
      .test(
        'no-special-chars-start-end',
        t("text.noSpecialCharsStartEnd"),
        function (value: any) {
          if (!value) return true;
          return !/^[^a-zA-Z0-9]|[^a-zA-Z0-9]$/.test(value);
        }
      )
      .test(
        'no-multiple-special-chars',
        t("text.noMultipleSpecialChars"),
        function (value: any) {
          if (!value) return true;
          return !/[\-\s&.,()]{2,}/.test(value);
        }
      ),
  });

  const formik = useFormik<SubDepartmentFormData>({
    initialValues: {
      subDeptID: 0,
      deptID: -1,
      subDeptName: "",
      isActive: true,
      createdBy: 0,
      createdDate: new Date().toISOString(),
      departmentName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Ensure subDeptID is 0 for add operation
      const submitValues = {
        ...values,
        subDeptName: sanitizeInput(values.subDeptName, 'subDeptName'),
        subDeptID: 0  // Always 0 for add
      };

      // Clear any existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      // Prevent multiple submissions
      if (isSubmitting) {
        toast.info(t("text.requestInProgress"));
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await axios.post(
          HOST_URL.HOST_URL + `SubDepartmentMaster/AddUpdateSubDepartmentMaster`,
          submitValues
        );

        if (response.data.isSuccess) {
          setToaster(false);
          toast.success(response.data.mesg);
          navigate('/master/SubDepartmentMaster');
        } else {
          setToaster(true);
          toast.error(response.data.mesg);
        }
      } catch (error: any) {
        console.error("Error submitting form:", error);
        if (error.response && error.response.status === 429) {
          toast.error(t("text.tooManyRequests"));
        } else if (error.response && error.response.status === 400) {
          toast.error(t("text.badRequest"));
        } else {
          toast.error(t("text.somethingWentWrong"));
        }
        setToaster(true);
      } finally {
        // Reset loading states after 2 seconds
        submitTimeoutRef.current = setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      }
    },
  });

  // Function to sanitize input
  const sanitizeInput = (value: string, fieldName: string) => {
    if (!value) return value;

    let sanitized = value;

    switch (fieldName) {
      case 'subDeptName':
        sanitized = value.replace(/[^a-zA-Z0-9\s\-&.,()]/g, '');
        sanitized = sanitized.replace(/([\-\s&.,()]){2,}/g, '$1');
        sanitized = sanitized.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');
        break;
      default:
        sanitized = value.replace(/[<>{}[\]\\]/g, '');
    }

    return sanitized;
  };

  // Custom onChange handler for real-time sanitization
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value, name);

    if (value !== sanitizedValue) {
      formik.setFieldValue(name, sanitizedValue);
    } else {
      formik.handleChange(e);
    }
  };

  const requiredFields = ['deptID', 'subDeptName'];

  const back = () => {
    navigate(-1);
  };

  // Fetch departments on component mount
  useEffect(() => {
    fetchDepartments();

    // Cleanup effect
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const collectData = {
        id: -1,
        nodeID: -1,
        titleID: -1,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `NewNodeMaster/GetNewNodeMaster`,
        collectData
      );

      if (response.data.isSuccess) {
        const departmentData = response.data.data;

        // Format options for Autocomplete
        const options = departmentData.map((item: Department) => ({
          value: String(item.id),
          label: item.name
        }));
        setDepartments(options);
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error(t("text.failedToLoadDepartments"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          padding: "-5px 5px",
          backgroundColor: "#ffffff",
          borderRadius: "5px",
          marginTop: "5px",
          border: ".5px solid #FF7722",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            textAlign="center"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            {t("text.CreateSubDeptMaster")}
          </Typography>

          <Grid item sm={4} xs={12}>
            <Typography style={{ marginTop: "-75px" }}>
              <Button
                type="button"
                onClick={back}
                variant="contained"
                style={{
                  marginBottom: 15,
                  marginTop: "45px",
                  backgroundColor: `var(--header-background)`,
                  width: 20,
                }}
                disabled={isSubmitting}
              >
                <ArrowBackSharpIcon />
              </Button>
            </Typography>
          </Grid>
          <Divider />
          <br />

          {isLoading && departments.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
              <CircularProgress />
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              {toaster === false ? "" : <ToastApp />}
              <Grid container spacing={2}>
                {/* Department Selection with Autocomplete */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    disablePortal
                    id="department-autocomplete"
                    options={departments}
                    fullWidth
                    size="small"
                    disabled={isSubmitting || isLoading}
                    onChange={(event, newValue: DepartmentOption | null) => {
                      if (newValue) {
                        formik.setFieldValue("deptID", parseInt(newValue.value));
                        formik.setFieldValue("departmentName", newValue.label);
                      } else {
                        formik.setFieldValue("deptID", -1);
                        formik.setFieldValue("departmentName", "");
                      }
                      formik.setFieldTouched("deptID", true);
                    }}
                    onBlur={() => formik.setFieldTouched("deptID", true)}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            {t("text.SelectDepartment")}
                            {requiredFields.includes('deptID') && (
                              <span style={{ color: formik.values.deptID > 0 ? 'green' : 'red' }}>*</span>
                            )}
                          </span>
                        }
                        error={formik.touched.deptID && Boolean(formik.errors.deptID)}
                      />
                    )}
                  />
                  {formik.touched.deptID && formik.errors.deptID ? (
                    <div style={{ color: "red", margin: "5px", fontSize: "12px" }}>
                      {formik.errors.deptID}
                    </div>
                  ) : null}
                </Grid>

                {/* Sub Department Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={
                      <span>
                        {t("text.EnterSubDeptName")}
                        {requiredFields.includes('subDeptName') && (
                          <span style={{ color: formik.values.subDeptName ? 'green' : 'red' }}>*</span>
                        )}
                      </span>
                    }
                    value={formik.values.subDeptName}
                    name="subDeptName"
                    id="subDeptName"
                    placeholder={t("text.EnterSubDeptName")}
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "white" }}
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    disabled={isSubmitting}
                    inputProps={{
                      maxLength: 50,
                      pattern: "[a-zA-Z0-9\\s\\-&.,()]*"
                    }}
                    error={formik.touched.subDeptName && Boolean(formik.errors.subDeptName)}
                  />
                  {formik.touched.subDeptName && formik.errors.subDeptName ? (
                    <div style={{ color: "red", margin: "5px", fontSize: "12px" }}>
                      {formik.errors.subDeptName}
                    </div>
                  ) : null}
                </Grid>

                {/* Status Switch */}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.isActive}
                        onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                        name="isActive"
                        color="primary"
                        disabled={isSubmitting}
                      />
                    }
                    label={
                      <span style={{ color: formik.values.isActive ? "green" : "red" }}>
                        {formik.values.isActive ? t("text.Active") : t("text.InActive")}
                      </span>
                    }
                  />
                </Grid>

                {/* Submit and Reset Buttons */}
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      style={{
                        backgroundColor: `var(--header-background)`,
                        color: "white",
                      }}
                      disabled={isSubmitting || !formik.isValid}
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {isSubmitting ? t("text.submitting") : t("text.save")}
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      style={{
                        backgroundColor: "#F43F5E",
                        color: "white",
                      }}
                      disabled={isSubmitting}
                      onClick={() => {
                        formik.resetForm({
                          values: {
                            subDeptID: 0,
                            deptID: -1,
                            subDeptName: "",
                            isActive: true,
                            createdBy: 0,
                            createdDate: new Date().toISOString(),
                            departmentName: "",
                          }
                        });
                      }}
                    >
                      {t("text.reset")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default SubDepartmentAdd;